import { useState, useRef, useEffect, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useVendor } from '../contexts/VendorContext';
import axios from 'axios';
import toast from 'react-hot-toast';

interface LocationBadgeData {
  simpleRFIDLocationName?: string | null;
  integrationLocationName?: string | null;
  integrationIcon?: string;
  onEditLocation?: () => void;
  onRefreshData?: () => void;
  isFlashing?: boolean;
}

interface HeaderProps {
  onIntegrationChange?: () => void;
  hasUnsavedData?: boolean;
  locationBadge?: LocationBadgeData;
}

export default function Header({ locationBadge }: HeaderProps) {
  const { user, logout, checkAuth } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { vendors, selectedVendor, selectedVendorId, setSelectedVendorId, isLoading: vendorsLoading } = useVendor();
  
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendorFilter, setVendorFilter] = useState('');
  const [showShipHeroModal, setShowShipHeroModal] = useState(false);
  const [shipheroUsername, setShipheroUsername] = useState('');
  const [shipheroPassword, setShipheroPassword] = useState('');
  const [shipheroLoading, setShipheroLoading] = useState(false);
  
  const settingsDropdownRef = useRef<HTMLDivElement>(null);
  const vendorFilterRef = useRef<HTMLInputElement>(null);
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target as Node)) {
        setShowSettingsDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set document title
  useEffect(() => {
    document.title = 'Simple RFID → ShipHero';
  }, []);

  const handleSaveShipHero = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShipheroLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/settings/shiphero-key`,
        { username: shipheroUsername, password: shipheroPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('ShipHero credentials saved successfully!');
      setShipheroUsername('');
      setShipheroPassword('');
      setShowShipHeroModal(false);
      await checkAuth();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save ShipHero credentials');
    } finally {
      setShipheroLoading(false);
    }
  };

  return (
    <>
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="w-[99%] px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14 relative">
            {/* Logo */}
            <div className="flex items-center w-[140px]">
              <img src="/logo.svg" alt="SimpleRFID" className="h-9 w-auto dark:hidden" />
              <img src="/logo-white.png" alt="SimpleRFID" className="h-12 w-[140px] object-contain hidden dark:block" />
            </div>

            {/* Center branding */}
            <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none">
              <img src="/jbay.svg" alt="JanelaBay" className="h-6 w-auto dark:invert" />
            </div>

            {/* Location badge inline - show current locations when set */}
            {locationBadge && (locationBadge.simpleRFIDLocationName || locationBadge.integrationLocationName) && (
              <div className="flex items-center ml-8">
                <button
                  onClick={locationBadge.onEditLocation}
                  className="flex items-center gap-3 px-4 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-sm"
                >
                  {locationBadge.simpleRFIDLocationName && (
                    <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                      <img src="/icon.png" alt="SimpleRFID" className="w-4 h-4 object-contain" />
                      <span className="max-w-[150px] truncate">{locationBadge.simpleRFIDLocationName}</span>
                    </div>
                  )}
                  {locationBadge.simpleRFIDLocationName && locationBadge.integrationLocationName && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  )}
                  {locationBadge.integrationLocationName && (
                    <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                      <img src="/shiphero_icon.svg" alt="ShipHero" className="w-4 h-4 object-contain" />
                      <span className="max-w-[150px] truncate">{locationBadge.integrationLocationName}</span>
                    </div>
                  )}
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                {locationBadge.onRefreshData && (
                  <button
                    onClick={locationBadge.onRefreshData}
                    className="ml-2 p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                    title="Refresh Data"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Right side - vendor selector + settings */}
            <div className="flex items-center ml-auto">
              <div className="relative" ref={settingsDropdownRef}>
                <button
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <img src="/icon.png" alt="SimpleRFID" className="w-4 h-4 object-contain" />
                  <div className="w-px h-4 bg-gray-200 dark:bg-gray-600"></div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200 max-w-[120px] truncate">
                    {vendorsLoading ? '...' : selectedVendor?.name || 'Select Vendor'}
                  </span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="5" cy="12" r="2" strokeWidth={1.5} />
                    <circle cx="19" cy="12" r="2" strokeWidth={1.5} />
                    <path strokeLinecap="round" strokeWidth={1.5} d="M7 12h10" />
                  </svg>
                  <img src="/shiphero_icon.svg" alt="ShipHero" className="w-5 h-5 object-contain" />
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${showSettingsDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showSettingsDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                    {/* User info header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{user?.username}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Signed in</p>
                    </div>

                    {/* SimpleRFID Vendor Section - Collapsible */}
                    <div className="mx-2 my-2 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                      <button
                        onClick={() => {
                          const newState = !showVendorList;
                          setShowVendorList(newState);
                          if (newState) {
                            setVendorFilter('');
                            setTimeout(() => vendorFilterRef.current?.focus(), 50);
                          }
                        }}
                        className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <img src="/icon.png" alt="SimpleRFID" className="w-4 h-4 object-contain" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Vendor</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-blue-600 dark:text-blue-400 max-w-[90px] truncate">{selectedVendor?.name || 'None'}</span>
                          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showVendorList ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {showVendorList && (
                        <div className="border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2">
                          <input
                            ref={vendorFilterRef}
                            type="text"
                            placeholder="Filter vendors..."
                            value={vendorFilter}
                            onChange={(e) => setVendorFilter(e.target.value)}
                            className="w-full px-2.5 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 mb-2"
                          />
                          <div className="space-y-1 max-h-32 overflow-y-auto pl-0.5 pr-2 py-0.5">
                            {vendors.length === 0 ? (
                              <p className="px-2 py-2 text-sm text-gray-400 dark:text-gray-500 italic">No vendors available</p>
                            ) : (
                              vendors
                                .filter(v => v.name.toLowerCase().includes(vendorFilter.toLowerCase()))
                                .map((vendor) => {
                                  const isSelected = selectedVendorId === vendor.id;
                                  return (
                                    <button
                                      key={vendor.id}
                                      onClick={() => {
                                        setSelectedVendorId(vendor.id);
                                        setShowVendorList(false);
                                        setVendorFilter('');
                                      }}
                                      className={`w-full px-2.5 py-1.5 flex items-center justify-between text-sm rounded-md transition-all duration-150 ${
                                        isSelected 
                                          ? 'bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-500' 
                                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      <span className="truncate">{vendor.name}</span>
                                      {isSelected && (
                                        <svg className="w-4 h-4 flex-shrink-0 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </button>
                                  );
                                })
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Settings Section */}
                    <div className="p-2">
                      {/* ShipHero Settings */}
                      <button
                        onClick={() => {
                          setShowShipHeroModal(true);
                          setShowSettingsDropdown(false);
                        }}
                        className="w-full px-3 py-2 flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <img src="/shiphero_icon.svg" alt="ShipHero" className="w-4 h-4 object-contain" />
                        <span>ShipHero Settings</span>
                        {user?.has_shiphero_key ? (
                          <span className="ml-auto text-xs text-green-600 dark:text-green-400">Connected</span>
                        ) : (
                          <span className="ml-auto text-xs text-amber-600 dark:text-amber-400">Not configured</span>
                        )}
                      </button>

                      {/* Theme Toggle */}
                      <button
                        onClick={toggleTheme}
                        className="w-full px-3 py-2 flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        {isDark ? (
                          <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                          </svg>
                        )}
                        <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="mx-3 border-t border-gray-200 dark:border-gray-700"></div>

                    {/* Logout Section */}
                    <div className="p-2">
                      <button
                        onClick={logout}
                        className="w-full px-3 py-2 flex items-center gap-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      {/* ShipHero Credentials Modal */}
      {showShipHeroModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowShipHeroModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <img src="/shiphero_icon.svg" alt="ShipHero" className="w-6 h-6" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">ShipHero Credentials</h2>
              </div>
              <button onClick={() => setShowShipHeroModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSaveShipHero} className="p-6 space-y-4">
              {user?.has_shiphero_key && (
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-green-800 dark:text-green-300 text-sm flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Credentials are configured. Enter new values to update.
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="sh-username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email / Username</label>
                <input
                  id="sh-username"
                  type="email"
                  value={shipheroUsername}
                  onChange={(e) => setShipheroUsername(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Enter your ShipHero email"
                  required
                />
              </div>
              <div>
                <label htmlFor="sh-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
                <input
                  id="sh-password"
                  type="password"
                  value={shipheroPassword}
                  onChange={(e) => setShipheroPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Enter your ShipHero password"
                  required
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Credentials are encrypted and used to obtain an access token.</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowShipHeroModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={shipheroLoading || !shipheroUsername || !shipheroPassword}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {shipheroLoading ? 'Saving...' : 'Save Credentials'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
